import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface BulkUpdatePayload {
  expenseIds: string[];
  updates: {
    organization_id?: string;
    property_id?: string;
    expense_type_id?: string;
    expense_subtype_id?: string;
    payment_type?: string;
  };
}

export const useBulkUpdateBusinessExpenses = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ expenseIds, updates }: BulkUpdatePayload) => {
      // Filtrar solo los campos que tienen valor (excluir "__NO_CHANGE__")
      const cleanedUpdates = Object.fromEntries(
        Object.entries(updates).filter(([_, value]) => value && value !== "__NO_CHANGE__")
      );

      if (Object.keys(cleanedUpdates).length === 0) {
        throw new Error("No hay cambios para aplicar");
      }

      // Actualizar todos los gastos en paralelo
      const updatePromises = expenseIds.map(id =>
        supabase
          .from("business_expenses")
          .update(cleanedUpdates)
          .eq("id", id)
      );

      const results = await Promise.all(updatePromises);

      // Verificar si hubo errores
      const errors = results.filter(result => result.error);
      if (errors.length > 0) {
        throw new Error(`${errors.length} egreso(s) no se pudieron actualizar`);
      }

      return results;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["business-expenses"], refetchType: "all" });
      queryClient.invalidateQueries({ queryKey: ["business-expenses-paginated"], refetchType: "all" });
      toast({
        title: "Éxito",
        description: `Se actualizaron ${variables.expenseIds.length} egreso(s) correctamente`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "No se pudieron actualizar los egresos",
        variant: "destructive",
      });
      console.error("Error bulk updating business expenses:", error);
    },
  });
};
